import { Container, Grid, Box, Typography, TextField } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, LineElement, PointElement, Title, BarElement } from 'chart.js';
import { Pie, Line, Bar } from 'react-chartjs-2';
import globals from "../globals";
import Chart from 'react-apexcharts';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const SUMMARY_CARDS = [
    { key: "upcoming_zoom_meetings_next_week", label: "Предстоящи Zoom срещи" },
    { key: "active_instructors", label: "Активни учители" },
    { key: "active_courses", label: "Активни курсове" },
    { key: "inactive_courses", label: "Неактивни курсове" },
];



ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title);

const optionsLine = {
    responsive: true,
    plugins: {
        legend: {
            position: 'top',
        },
        title: {
            display: true,
            text: 'Продажби по седмици (в евро)',
        },
    },
};

const optionsHorizontal = {
    indexAxis: 'y',
    elements: {
        bar: {
            borderWidth: 2,
        },
    },
    responsive: true,
    plugins: {
        legend: {
            display: false,
        },
        title: {
            display: true,
            text: 'Разпределение на ученици по курсове',
        },
    },
};


const heatmapOptions = {
    xaxis: { categories: ['Понеделник', 'Вторник', 'Сряда', 'Четвъртък', 'Петък', 'Събота', 'Неделя'] },
    chart: { height: 350, type: 'heatmap' },
    dataLabels: { enabled: false },
    colors: ["#ff9f40"],
    title: { text: 'Часове по натовареност в Zoom' },
};

const LINE_COLORS = [
    'rgb(255, 99, 132)',
    'rgb(53, 162, 235)',
    'rgb(75, 192, 192)',
    'rgb(255, 159, 64)',
    'rgb(153, 102, 255)',
    'rgb(255, 205, 86)',
    'rgb(201, 203, 207)',
];

function toISODate(d) {
    return d.toISOString().slice(0, 10);
}

const today = new Date();
const oneMonthAgo = new Date(today);
oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

const DashboardPage = () => {
    const { token } = useAuth();
    const [categoryRows, setCategoryRows] = useState([]);
    const [startDate, setStartDate] = useState(toISODate(oneMonthAgo));
    const [endDate, setEndDate] = useState(toISODate(today));
    const [dataLine, setDataLine] = useState({ labels: [], datasets: [] });
    const [dataHorizontal, setDataHorizontal] = useState({ labels: [], datasets: [] });
    const [dataPie, setDataPie] = useState({ labels: [], datasets: [] });
    const [heatmapSeries, setHeatmapSeries] = useState([]);
    const [weekOffset, setWeekOffset] = useState(0);
    const [weekMonday, setWeekMonday] = useState('');
    const [summaryStats, setSummaryStats] = useState(null);
    const [totalRevenue, setTotalRevenue] = useState(null);

    useEffect(() => {
        axios.get(`${globals.apiUrl}/admin/analytics/dashboard-summary`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            setSummaryStats(res.data);
        }).catch((err) => {
            console.error('Failed to fetch dashboard summary', err);
        });
    }, [token]);

    useEffect(() => {
        axios.get(`${globals.apiUrl}/admin/analytics/categories`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            setCategoryRows(res.data);
        }).catch((err) => {
            console.error('Failed to fetch category stats', err);
        });
    }, [token]);

    useEffect(() => {
        if (!startDate || !endDate) return;
        axios.get(`${globals.apiUrl}/admin/analytics/weekly-sales`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { start_date: startDate, end_date: endDate },
        }).then((res) => {
            const { weeks, instructors, total_revenue } = res.data;
            setTotalRevenue(total_revenue ?? null);
            setDataLine({
                labels: weeks,
                datasets: instructors.map((inst, index) => {
                    const color = LINE_COLORS[index % LINE_COLORS.length];
                    return {
                        label: inst.name,
                        data: inst.sales,
                        borderColor: color,
                        backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.3)'),
                        tension: 0.3,
                        pointRadius: 2,
                    };
                }),
            });
        }).catch((err) => {
            console.error('Failed to fetch weekly sales', err);
        });
    }, [token, startDate, endDate]);

    useEffect(() => {
        axios.get(`${globals.apiUrl}/admin/analytics/student-distribution`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            setDataHorizontal({
                labels: res.data.labels,
                datasets: [{
                    data: res.data.data,
                    borderColor: 'rgba(75, 192, 192, 1)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                }],
            });
        }).catch((err) => {
            console.error('Failed to fetch student distribution', err);
        });
    }, [token]);

    useEffect(() => {
        axios.get(`${globals.apiUrl}/admin/analytics/registered-vs-enrolled`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            const { no_course, courses } = res.data;
            const labels = ['Без курс', ...courses.map(c => c.title)];
            const data = [no_course, ...courses.map(c => c.count)];
            const backgroundColors = [
                'rgba(255, 99, 132, 0.2)',
                'rgba(54, 162, 235, 0.2)',
                'rgba(255, 206, 86, 0.2)',
                'rgba(75, 192, 192, 0.2)',
                'rgba(153, 102, 255, 0.2)',
                'rgba(255, 159, 64, 0.2)',
                'rgba(201, 203, 207, 0.2)',
            ];
            const borderColors = backgroundColors.map(c => c.replace('0.2)', '1)'));
            setDataPie({
                labels,
                datasets: [{
                    label: 'Ученици',
                    data,
                    backgroundColor: labels.map((_, i) => backgroundColors[i % backgroundColors.length]),
                    borderColor: labels.map((_, i) => borderColors[i % borderColors.length]),
                    borderWidth: 1,
                }],
            });
        }).catch((err) => {
            console.error('Failed to fetch registered vs enrolled', err);
        });
    }, [token]);

    useEffect(() => {
        axios.get(`${globals.apiUrl}/admin/analytics/zoom-heatmap`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { week_offset: weekOffset },
        }).then((res) => {
            setHeatmapSeries(res.data.series);
            setWeekMonday(res.data.week_monday);
        }).catch((err) => {
            console.error('Failed to fetch zoom heatmap', err);
        });
    }, [token, weekOffset]);

    return (
        <Container>
            <Grid container justifyContent={'space-around'}>
                {SUMMARY_CARDS.map(({ key, label }) => (
                    <Grid item key={key} justifyContent={'center'} textAlign={'center'}>
                        <Box
                            sx={{
                                width: 200,
                                height: 100,
                                borderRadius: 1,
                                border: 2,
                            }}
                        >
                            <Typography sx={{ fontSize: 15 }}>{label}</Typography>
                            <Typography sx={{ mt: 1, fontSize: 40, fontWeight: 'bold' }}>
                                {summaryStats ? summaryStats[key] : '…'}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            <Grid container>
                <Grid item sm={8}>
                <Box sx={{ mt: 5 }}>
                    <TableContainer component={Paper}>
                        <Table size="small" aria-label="categories table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>№</TableCell>
                                    <TableCell>Категория</TableCell>
                                    <TableCell>Брой инструктори</TableCell>
                                    <TableCell align="right">Инструктори</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {categoryRows.map((row) => (
                                    <TableRow key={row.number}>
                                        <TableCell component="th" scope="row">{row.number}</TableCell>
                                        <TableCell>{row.category}</TableCell>
                                        <TableCell>{row.num_instructors}</TableCell>
                                        <TableCell align="right">{row.instructors.join(', ')}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                </Grid>
            </Grid>

            <Grid container sx={{ mt: 5 }} spacing={5} alignItems="center">
                <Grid item>
                    <TextField
                        type="date"
                        label="Начало"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item>
                    -
                </Grid>
                <Grid item>
                    <TextField
                        type="date"
                        label="Край"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item sx={{ ml: 2 }}>
                    <Typography sx={{ fontSize: 14, color: globals.colors.gray }}>
                        Приходи от курсове
                    </Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 'bold' }}>
                        {totalRevenue != null
                            ? `€${totalRevenue.toLocaleString('bg-BG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '…'}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container sx={{ mt: 3 }} spacing={3} justifyContent={'center'}>
                <Grid item sm={8} sx={{ mb: 5 }}>
                    <Line options={optionsLine} data={dataLine} />
                </Grid>
                <Grid item sm={7} sx={{ mt: 5 }}>
                    <Bar options={optionsHorizontal} data={dataHorizontal} />
                </Grid>
                <Grid item sm={4} textAlign={'center'}>
                    <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: globals.colors.gray }}>Регистрирани ученици vs Ученици със записан курс</Typography>
                    <Pie data={dataPie} />
                </Grid>
                <Grid item sx={{ mt: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1 }}>
                        <Typography
                            onClick={() => setWeekOffset(o => Math.min(o + 1, 3))}
                            sx={{ cursor: weekOffset < 3 ? 'pointer' : 'default', opacity: weekOffset < 3 ? 1 : 0.3, fontWeight: 'bold' }}
                        >
                            ‹
                        </Typography>
                        <Typography sx={{ fontSize: 13 }}>
                            Седмица от {weekMonday}
                        </Typography>
                        <Typography
                            onClick={() => setWeekOffset(o => Math.max(o - 1, 0))}
                            sx={{ cursor: weekOffset > 0 ? 'pointer' : 'default', opacity: weekOffset > 0 ? 1 : 0.3, fontWeight: 'bold' }}
                        >
                            ›
                        </Typography>
                    </Box>
                    <Chart options={heatmapOptions} series={heatmapSeries} type="heatmap" height={350} width={700}/>
                </Grid>
            </Grid>
        </Container>
    );
}

export default DashboardPage